import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Plus, Sparkles, BarChart3, Play, Pause, MoreVertical,
  ChevronRight, Bot, Settings, FileText, Globe, Building2,
  TrendingUp, Calendar, FolderOpen, Image, Video, Trash2, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import AgentConsentModal from "../components/AgentConsentModal";
import KillSwitch from "../components/KillSwitch";

// Dashboard Components
import {
  StatsGrid,
  ConnectedPlatforms,
  SEOPanel,
  CulturalPanel,
  AgencyPanel,
  VoiceCommandModal,
  AnalyticsPanel,
  CalendarPanel,
  SettingsPanel,
  ReportsPanel
} from "../components/dashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_ICONS = {
  tiktok: "🎵",
  instagram: "📸",
  linkedin: "💼",
  twitter: "🐦",
  youtube: "▶️"
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    total_content: 0,
    scheduled_posts: 0,
    published_posts: 0
  });
  const [campaigns, setCampaigns] = useState([]);
  const [campaignContent, setCampaignContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Agent consent state
  const [showAgentConsent, setShowAgentConsent] = useState(false);
  const [agentConsentGiven, setAgentConsentGiven] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Connected platforms state
  const [connectedPlatforms, setConnectedPlatforms] = useState({
    youtube: false,
    linkedin: false,
    twitter: false,
    instagram: false,
    facebook: false,
    tiktok: false
  });
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  
  // Modal states
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  
  // Campaign selector state
  const [selectedCampaign, setSelectedCampaign] = useState("all");

  useEffect(() => {
    fetchData();
    checkAgentConsent();
    checkConnectedPlatforms();
    handleOAuthCallbacks();
  }, [searchParams]);

  const handleOAuthCallbacks = () => {
    const errorMessage = searchParams.get('message');
    
    // YouTube
    const youtubeConnected = searchParams.get('youtube_connected');
    const youtubeError = searchParams.get('youtube_error');
    if (youtubeConnected === 'true') {
      const channelName = searchParams.get('channel');
      toast.success(`YouTube connected! Channel: ${channelName || 'Your Channel'}`);
      setConnectedPlatforms(prev => ({ ...prev, youtube: true }));
      window.history.replaceState({}, '', '/dashboard');
    } else if (youtubeError) {
      toast.error(youtubeError === 'not_logged_in' ? errorMessage || 'Please log in first' : `YouTube: ${youtubeError}`);
      window.history.replaceState({}, '', '/dashboard');
    }
    
    // LinkedIn
    const linkedinConnected = searchParams.get('linkedin_connected');
    const linkedinError = searchParams.get('linkedin_error');
    if (linkedinConnected === 'true') {
      toast.success('LinkedIn connected successfully!');
      setConnectedPlatforms(prev => ({ ...prev, linkedin: true }));
      window.history.replaceState({}, '', '/dashboard');
    } else if (linkedinError) {
      toast.error(linkedinError === 'not_logged_in' ? errorMessage || 'Please log in first' : `LinkedIn: ${linkedinError}`);
      window.history.replaceState({}, '', '/dashboard');
    }
    
    // TikTok
    const tiktokConnected = searchParams.get('tiktok_connected');
    const tiktokError = searchParams.get('tiktok_error');
    if (tiktokConnected === 'true') {
      const tiktokName = searchParams.get('name');
      toast.success(`TikTok connected! ${tiktokName ? `Account: ${tiktokName}` : ''}`);
      setConnectedPlatforms(prev => ({ ...prev, tiktok: true }));
      window.history.replaceState({}, '', '/dashboard');
    } else if (tiktokError) {
      toast.error(`TikTok: ${tiktokError}`);
      window.history.replaceState({}, '', '/dashboard');
    }
    
    // Meta (Facebook/Instagram)
    const metaConnected = searchParams.get('meta_connected');
    const metaError = searchParams.get('meta_error');
    if (metaConnected === 'true') {
      const platforms = searchParams.get('platforms');
      toast.success(`Connected: ${platforms || 'Facebook/Instagram'}`);
      setConnectedPlatforms(prev => ({ 
        ...prev, 
        facebook: platforms?.includes('Facebook') || false,
        instagram: platforms?.includes('Instagram') || false
      }));
      window.history.replaceState({}, '', '/dashboard');
    } else if (metaError) {
      toast.error(`Meta: ${metaError}`);
      window.history.replaceState({}, '', '/dashboard');
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/campaigns/`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data || {});
      const campaignsData = Array.isArray(campaignsRes.data) ? campaignsRes.data : [];
      setCampaigns(campaignsData);
      
      // Fetch content for selected campaign if one is selected
      if (selectedCampaign && selectedCampaign !== "all") {
        const contentRes = await axios.get(`${API}/campaigns/${selectedCampaign}/content`, { headers }).catch(() => ({ data: [] }));
        setCampaignContent(contentRes.data || []);
      } else {
        setCampaignContent([]);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refetch when campaign selection changes
  useEffect(() => {
    if (selectedCampaign) {
      fetchData();
    }
  }, [selectedCampaign]);

  const checkAgentConsent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/consent-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentConsentGiven(response.data.agent_consent_given);
    } catch (error) {
      console.error('Failed to check consent:', error);
    }
  };

  const checkConnectedPlatforms = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id;
      
      if (!token || !userId) return;
      
      // Check OAuth platforms from user profile
      const response = await axios.get(`${API}/auth/me?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const connections = response.data.platform_connections || [];
      
      // All platforms should only show as connected if user has actually connected them
      const platformStatus = {
        youtube: connections.some(c => c.platform === 'youtube' && c.connected),
        linkedin: connections.some(c => c.platform === 'linkedin' && c.connected),
        twitter: connections.some(c => c.platform === 'twitter' && c.connected),
        instagram: connections.some(c => c.platform === 'instagram' && c.connected),
        facebook: connections.some(c => c.platform === 'facebook' && c.connected),
        tiktok: connections.some(c => c.platform === 'tiktok' && c.connected)
      };
      
      setConnectedPlatforms(platformStatus);
    } catch (error) {
      console.error('Error fetching platform connections:', error);
      // Don't default anything to true on error - show all as disconnected
      setConnectedPlatforms({
        youtube: false,
        linkedin: false,
        twitter: false,
        instagram: false,
        facebook: false,
        tiktok: false
      });
    }
  };

  const connectPlatform = async (platform) => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;
    
    if (!token || !userId) {
      toast.error('Please log in to connect platforms');
      navigate('/login?redirect=/dashboard&action=connect&platform=' + platform);
      return;
    }
    
    setConnectingPlatform(platform);
    
    const oauthUrls = {
      youtube: `${API}/auth/youtube/?user_id=${userId}`,
      linkedin: `${API}/auth/linkedin?user_id=${userId}`,
      twitter: `${API}/auth/twitter?user_id=${userId}`,
      instagram: `${API}/auth/meta?user_id=${userId}`,
      facebook: `${API}/auth/meta?user_id=${userId}`,
      tiktok: `${API}/auth/tiktok?user_id=${userId}`
    };
    
    const url = oauthUrls[platform];
    if (url) {
      // Twitter uses API keys, not OAuth redirect - handle differently
      if (platform === 'twitter') {
        try {
          const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            toast.success(response.data.message || 'Twitter connected successfully!');
            setConnectedPlatforms(prev => ({ ...prev, twitter: true }));
            await checkConnectedPlatforms();
          } else {
            toast.error(response.data.error || 'Failed to connect Twitter');
          }
        } catch (error) {
          console.error('Twitter connect error:', error);
          // If it shows connected already, don't show error
          if (!connectedPlatforms.twitter) {
            toast.error('Failed to connect Twitter');
          }
        }
        setConnectingPlatform(null);
        return;
      }
      
      // Other platforms use OAuth redirect
      window.location.href = url;
    } else {
      toast.info(`${platform.charAt(0).toUpperCase() + platform.slice(1)} uses API keys - already configured!`);
      setConnectingPlatform(null);
    }
  };

  const disconnectPlatform = async (platform) => {
    try {
      const token = localStorage.getItem('token');
      
      // Facebook and Instagram use the Meta OAuth endpoint
      const endpoint = (platform === 'facebook' || platform === 'instagram') 
        ? `${API}/auth/meta/disconnect`
        : `${API}/auth/${platform}/disconnect`;
      
      const response = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check for success - handle both response.data.success and HTTP 200 status
      if (response.status === 200 || response.data?.success) {
        const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
        toast.success(`${platformName} disconnected successfully`);
        
        // For Meta, disconnect both Facebook and Instagram
        if (platform === 'facebook' || platform === 'instagram') {
          setConnectedPlatforms(prev => ({ ...prev, facebook: false, instagram: false }));
        } else {
          setConnectedPlatforms(prev => ({ ...prev, [platform]: false }));
        }
        // Refresh connections after a short delay
        setTimeout(() => checkConnectedPlatforms(), 500);
      } else {
        toast.error(`Failed to disconnect ${platform}`);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      // Check if it's actually a success that axios misinterpreted
      if (error.response?.status === 200 || error.response?.data?.success) {
        const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
        toast.success(`${platformName} disconnected successfully`);
        if (platform === 'facebook' || platform === 'instagram') {
          setConnectedPlatforms(prev => ({ ...prev, facebook: false, instagram: false }));
        } else {
          setConnectedPlatforms(prev => ({ ...prev, [platform]: false }));
        }
        setTimeout(() => checkConnectedPlatforms(), 500);
      } else {
        toast.error(`Failed to disconnect ${platform}`);
      }
    }
  };

  const handleConsentSuccess = () => {
    setAgentConsentGiven(true);
    setShowAgentConsent(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const requireConsent = (action) => {
    if (agentConsentGiven) {
      action();
    } else {
      setPendingAction(() => action);
      setShowAgentConsent(true);
    }
  };

  // Get stats based on campaign selection
  const getDisplayStats = () => {
    if (selectedCampaign === "all") {
      return stats;
    }
    
    // If a specific campaign is selected, calculate stats from content
    const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);
    const contentCount = campaignContent.length;
    const scheduledCount = campaignContent.filter(c => c.status === 'scheduled').length;
    const publishedCount = campaignContent.filter(c => c.status === 'published').length;
    
    return {
      status: selectedCampaignData?.status || 'active',
      total_content: contentCount,
      scheduled_posts: scheduledCount,
      published_posts: publishedCount
    };
  };

  // Delete a campaign
  const deleteCampaign = async (campaignId) => {
    setDeletingCampaign(campaignId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Campaign deleted (${response.data.content_deleted || 0} content pieces removed)`);
        // Reset selection if deleted campaign was selected
        if (selectedCampaign === campaignId) {
          setSelectedCampaign("all");
        }
        // Refresh data
        fetchData();
      } else {
        throw new Error(response.data.error || 'Delete failed');
      }
    } catch (error) {
      console.error("Campaign delete error:", error);
      toast.error(error.response?.data?.detail || error.message || "Failed to delete campaign");
    } finally {
      setDeletingCampaign(null);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Sidebar 
        onVoiceCommand={() => setShowVoiceModal(true)}
        onNewCampaign={() => requireConsent(() => setShowVoiceModal(true))}
      />
      
      <main className="md:ml-64 min-h-screen p-6 md:p-8 text-foreground">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground">Manage your AI marketing campaigns</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Campaign Selector - Always visible */}
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-[200px] bg-white/5 border-white/10" data-testid="campaign-selector">
                <SelectValue placeholder="All Campaigns" />
              </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            <KillSwitch />
            <Button 
              onClick={() => requireConsent(() => setShowVoiceModal(true))}
              data-testid="new-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mb-6">
          <StatsGrid 
            stats={getDisplayStats()} 
            loading={loading} 
            selectedCampaignName={selectedCampaign !== "all" ? campaigns.find(c => c.id === selectedCampaign)?.name : null}
          />
        </div>

        {/* Main Content with Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary/20">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-primary/20">
              <TrendingUp className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="calendar" className="data-[state=active]:bg-primary/20">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="seo" className="data-[state=active]:bg-primary/20">
              <FileText className="w-4 h-4 mr-2" />
              SEO
            </TabsTrigger>
            <TabsTrigger value="cultural" className="data-[state=active]:bg-primary/20">
              <Globe className="w-4 h-4 mr-2" />
              Cultural
            </TabsTrigger>
            <TabsTrigger value="agency" className="data-[state=active]:bg-primary/20">
              <Building2 className="w-4 h-4 mr-2" />
              Agency
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-primary/20">
              <FileText className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-primary/20">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Connected Platforms */}
              <div className="lg:col-span-2">
                <ConnectedPlatforms 
                  connectedPlatforms={connectedPlatforms}
                  connectingPlatform={connectingPlatform}
                  onConnect={connectPlatform}
                  onDisconnect={disconnectPlatform}
                />
              </div>

              {/* Quick Actions */}
              <Card className="glass-card border-white/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setShowVoiceModal(true)}
                    data-testid="voice-command-btn"
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    Voice Command
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => requireConsent(() => setShowVoiceModal(true))}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Campaign
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setActiveTab("seo")}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    SEO Tools
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Campaigns List */}
            <Card className="glass-card border-white/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {selectedCampaign !== "all" ? "Selected Campaign" : "Recent Campaigns"}
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="text-xs">
                    View All <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const displayCampaigns = selectedCampaign !== "all" 
                    ? campaigns.filter(c => c.id === selectedCampaign) 
                    : campaigns.slice(0, 5);
                  
                  return displayCampaigns.length > 0 ? (
                    <div className="space-y-3">
                      {displayCampaigns.map((campaign) => (
                      <motion.div
                        key={campaign.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{campaign.name}</p>
                              <Badge variant={campaign.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                                {campaign.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {campaign.company_name} • {campaign.industry}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              {campaign.platforms?.map((p) => (
                                <span key={p} className="text-sm">{PLATFORM_ICONS[p]}</span>
                              ))}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="glass border-white/10">
                                <DropdownMenuItem onClick={() => setSelectedCampaign(campaign.id)}>
                                  <FolderOpen className="w-3 h-3 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/content-library')}>
                                  <Image className="w-3 h-3 mr-2" />
                                  View Content
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Play className="w-3 h-3 mr-2" />
                                  {campaign.status === 'active' ? 'Pause' : 'Start'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem 
                                      onSelect={(e) => e.preventDefault()}
                                      className="text-red-400 focus:text-red-400"
                                    >
                                      {deletingCampaign === campaign.id ? (
                                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                      ) : (
                                        <Trash2 className="w-3 h-3 mr-2" />
                                      )}
                                      Delete Campaign
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="glass border-white/10">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete &quot;{campaign.name}&quot; and all its content. This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => deleteCampaign(campaign.id)}
                                        className="bg-red-500 hover:bg-red-600"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No campaigns yet</p>
                      <p className="text-sm">Create your first AI marketing campaign!</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsPanel />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <CalendarPanel />
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SEOPanel />
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg">SEO Best Practices</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>✓ Convert social content to 1000+ word blog posts</p>
                  <p>✓ Auto-generate meta titles & descriptions</p>
                  <p>✓ Proper heading structure (H1, H2, H3)</p>
                  <p>✓ Keyword optimization</p>
                  <p>✓ Multi-language translation</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Cultural Tab */}
          <TabsContent value="cultural">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CulturalPanel />
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg">Cultural Adaptation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>✓ 12 cultural contexts supported</p>
                  <p>✓ Region-specific tone adjustment</p>
                  <p>✓ Holiday awareness for scheduling</p>
                  <p>✓ Cultural sensitivity checking</p>
                  <p>✓ Avoid topic warnings</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Agency Tab */}
          <TabsContent value="agency">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AgencyPanel />
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg">Agency Features</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>✓ Manage multiple client accounts</p>
                  <p>✓ Create social accounts for clients</p>
                  <p>✓ Secure encrypted credential storage</p>
                  <p>✓ Client-specific campaigns</p>
                  <p>✓ White-label reporting</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <ReportsPanel />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <VoiceCommandModal 
        isOpen={showVoiceModal} 
        onClose={() => setShowVoiceModal(false)}
        onSuccess={fetchData}
      />
      
      <AgentConsentModal 
        isOpen={showAgentConsent}
        onClose={() => setShowAgentConsent(false)}
        onSuccess={handleConsentSuccess}
      />
    </div>
  );
}
